export class EmailPreferences {
    readonly isWeeklyPromotionalEmails: boolean;
    readonly isMonthlyNewsLetter: boolean;
    readonly isProductUpdates: boolean;
    readonly isTechnicalUpdates: boolean;
    readonly isProductFeedback: boolean;
    readonly isWebinarAndEventUpdates: boolean;

    constructor(startingValues?: Partial<EmailPreferences>) {
        Object.assign(this, startingValues);
    }

    makeUpdate() {
        return new EmailPreferencesUpdate({
            isWeeklyPromotionalEmails: this.isWeeklyPromotionalEmails,
            isMonthlyNewsLetter: this.isMonthlyNewsLetter,
            isProductUpdates: this.isProductUpdates,
            isTechnicalUpdates: this.isTechnicalUpdates,
            isProductFeedback: this.isProductFeedback,
            isWebinarAndEventUpdates: this.isWebinarAndEventUpdates,
            originalObject: this
        });
    }
}

export class EmailPreferencesUpdate {
    isWeeklyPromotionalEmails: boolean;
    isMonthlyNewsLetter: boolean;
    isProductUpdates: boolean;
    isTechnicalUpdates: boolean;
    isProductFeedback: boolean;
    isWebinarAndEventUpdates: boolean;
    originalObject: EmailPreferences;

    constructor(startingValues?: Partial<EmailPreferencesUpdate>) {
        Object.assign(this, startingValues);
    }

    allChecked() {
        return (
            this.isWeeklyPromotionalEmails &&
            this.isMonthlyNewsLetter &&
            this.isProductUpdates &&
            this.isTechnicalUpdates &&
            this.isProductFeedback &&
            this.isWebinarAndEventUpdates
        );
    }

    checkAll(check: boolean) {
        this.isWeeklyPromotionalEmails = check;
        this.isMonthlyNewsLetter = check;
        this.isProductUpdates = check;
        this.isTechnicalUpdates = check;
        this.isProductFeedback = check;
        this.isWebinarAndEventUpdates = check;
    }

    equals(other: EmailPreferences | EmailPreferencesUpdate): boolean {
        return (
            this.isWeeklyPromotionalEmails ===
                other.isWeeklyPromotionalEmails &&
            this.isMonthlyNewsLetter === other.isMonthlyNewsLetter &&
            this.isProductUpdates === other.isProductUpdates &&
            this.isTechnicalUpdates === other.isTechnicalUpdates &&
            this.isProductFeedback === other.isProductFeedback &&
            this.isWebinarAndEventUpdates === other.isWebinarAndEventUpdates
        );
    }
}

export class CompanyAssessmentSettings {
    readonly baDaysBeforeExpire: number;
    readonly jaDaysBeforeExpire: number;
    readonly showCaOfJa: boolean;
    readonly showOrqInBa: boolean;
    readonly enableConsentInBa: boolean;

    constructor(startingValues?: Partial<CompanyAssessmentSettings>) {
        Object.assign(this, startingValues);
    }

    makeUpdate() {
        return new CompanyAssessmentSettingsUpdate(this);
    }
}

export class CompanyAssessmentSettingsUpdate {
    baDaysBeforeExpire: number;
    jaDaysBeforeExpire: number;
    showCaOfJa: boolean;
    showOrqInBa: boolean;
    enableConsentInBa: boolean;

    constructor(startingValues?: Partial<CompanyAssessmentSettingsUpdate>) {
        Object.assign(this, startingValues);
    }
}

export class CompanySettings {
    readonly companyId: string;
    readonly companyName: string;
    readonly allowPiReports: boolean;
    readonly allowProReports: boolean;
    readonly piSessionExpirationDays: number;
    readonly proSessionExpirationDays: number;
    readonly allowPowerUserCreateTopLevelFolders: boolean;
    readonly timeZone: string;
    readonly languageLocale: string;
    readonly isOptionalResearchQuestionShown: boolean;
    readonly hasApiAccess: boolean;
    readonly hasCognitiveJAAccess: boolean;
    readonly oiAssessmentStatusWebhookUrl: string;
    readonly employeeCount: number;
    readonly isBehavioralConsentNeeded: boolean;
    readonly consentText: string;
    readonly includeCogSampleLink: boolean;
    readonly isMfaEnabled: boolean; // updated via mfa endpoint
    readonly disableAiInterviewFeedbackSummary: false;
    readonly disableAiAssistant: false;

    constructor(startingValues?: Partial<CompanySettings>) {
        Object.assign(this, startingValues);
    }

    makeUpdate() {
        return new CompanySettingsUpdate({
            allowPiReports: this.allowPiReports,
            allowProReports: this.allowProReports,
            piSessionExpirationDays: this.piSessionExpirationDays,
            proSessionExpirationDays: this.proSessionExpirationDays,
            allowPowerUserCreateTopLevelFolders:
                this.allowPowerUserCreateTopLevelFolders,
            timeZone: this.timeZone,
            languageLocale: this.languageLocale,
            isOptionalResearchQuestionShown:
                this.isOptionalResearchQuestionShown,
            hasApiAccess: this.hasApiAccess,
            hasCognitiveJAAccess: this.hasCognitiveJAAccess,
            oiAssessmentStatusWebhookUrl: this.oiAssessmentStatusWebhookUrl,
            employeeCount: this.employeeCount,
            isBehavioralConsentNeeded: this.isBehavioralConsentNeeded,
            consentText: this.consentText,
            includeCogSampleLink: this.includeCogSampleLink,
            originalObject: this
        });
    }
}

export class CompanySettingsUpdate {
    allowPiReports: boolean;
    allowProReports: boolean;
    piSessionExpirationDays: number;
    proSessionExpirationDays: number;
    allowPowerUserCreateTopLevelFolders: boolean;
    timeZone: string;
    languageLocale: string;
    isOptionalResearchQuestionShown: boolean;
    hasApiAccess: boolean;
    hasCognitiveJAAccess: boolean;
    oiAssessmentStatusWebhookUrl: string;
    employeeCount: number;
    isBehavioralConsentNeeded: boolean;
    consentText: string;
    originalObject: CompanySettings;
    includeCogSampleLink: boolean;

    constructor(startingValues?: Partial<CompanySettingsUpdate>) {
        Object.assign(this, startingValues);
    }
}

/**
 * New company settings from V5 companysettings api
 */
export type companySettingsResponse = {
    data: companySettings;
    errors: string[];
    isSuccess: boolean;
};

/**
 * New company settings from V5 companysettings api
 */
export type companySettings = {
    companyGuid: string;
    arePronounsEnabled: boolean;
    areAvatarsEnabled: boolean;
    isNameEditingEnabled: boolean;
    isPreferredNameEnabled: boolean;
    domainAllowList: string[];
    isAllowNonDomainEnabled?: boolean;
    departmentList: Array<[string, number]>;
    baSessionExpirationDays: number;
    proSessionExpirationDays: number;
    isOptionalResearchQuestionsShown: boolean;
    isBehavioralConsentNeeded: boolean;
    consentText: string;
    languageLocale: string;
    oiAssessmentStatusWebHookUrl: string;
    employeeAccessRulesEnabled: boolean;
    isAccountPaused: boolean;
    disableAiAssistant: boolean;
};
